import { t } from '../../i18n/es';
import { SLOT_IDS, type SlotId } from '../../save/schema';
import type { SaveStore } from '../../save/save-store';
import { slotCard } from './slot-card';

export interface SlotScreenOptions {
  store: () => SaveStore;
  /** A game is ready in a slot: the flow goes on to MISIONES (#54). */
  onSlotReady: (slotId: SlotId) => void;
}

/**
 * EXPEDIENTES (GDD §13.2, §6.3): one card per slot, in order. Occupied slots offer "Continuar" and
 * "Eliminar" (with a confirmation dialog whose destructive button is the only red of the screen); empty
 * ones offer "Nueva partida". Native elements until the design system components land (#15).
 */
export function expedientesScreen(options: SlotScreenOptions): HTMLElement {
  const section = document.createElement('section');
  section.className = 'slots';
  section.setAttribute('aria-labelledby', 'slots-title');

  const render = (focus?: string) => {
    const save = options.store().save;
    const title = `<h1 id="slots-title" class="tactical">${t('ui.screen.expedientes')}</h1>`;
    const cards = SLOT_IDS.map((slotId) => {
      const card = slotCard(save, slotId);
      const actions = card.empty
        ? `<button type="button" class="button" data-action="new" aria-label="${t('ui.expedientes.newGame')} ${slotId}">${t('ui.expedientes.newGame')}</button>`
        : `<button type="button" class="button" data-action="continue" aria-label="${t('ui.expedientes.continue')} ${slotId}">${t('ui.expedientes.continue')}</button>
           <button type="button" class="button" data-action="delete" aria-label="${t('ui.expedientes.delete')} ${slotId}">${t('ui.expedientes.delete')}</button>`;
      const body = card.empty
        ? `<p class="muted">${t('ui.expedientes.empty')}</p>`
        : `<p class="display-mono" data-field="mission">${card.mission}</p>
           <p class="readout" data-field="lives">${card.lives}</p>
           <p class="readout" data-field="points">${card.points}</p>
           <p class="readout muted" data-field="date">${card.date}</p>`;
      return `<article class="slot-card" data-slot="${slotId}" aria-label="${slotId}">
        <p class="tactical">${slotId}</p>${body}<div class="slot-actions">${actions}</div></article>`;
    }).join('');
    section.innerHTML = title + cards;

    const target =
      (focus && section.querySelector<HTMLElement>(`[aria-label="${focus}"]`)) ||
      (save.lastSlotId && section.querySelector<HTMLElement>(`[data-slot="${save.lastSlotId}"] [data-action="continue"]`)) ||
      section.querySelector<HTMLElement>('[data-action="new"]') ||
      section.querySelector<HTMLElement>('button');
    queueMicrotask(() => target?.focus());
  };

  const confirmDelete = (slotId: SlotId, opener: HTMLElement) => {
    const dialog = document.createElement('dialog');
    dialog.className = 'dialog';
    dialog.setAttribute('aria-labelledby', 'delete-title');
    dialog.innerHTML = `
      <h2 id="delete-title" class="panel-title">${t('ui.expedientes.delete.title')}</h2>
      <p class="body">${t('ui.expedientes.delete.body', { slot: slotId })}</p>
      <div class="slot-actions">
        <button type="button" class="button" data-choice="cancel">${t('ui.expedientes.delete.cancel')}</button>
        <button type="button" class="button button--destructive" data-choice="confirm">${t('ui.expedientes.delete.confirm')}</button>
      </div>`;
    const close = (deleted: boolean) => {
      if (!dialog.open) return;
      dialog.close();
      dialog.remove();
      if (deleted) {
        options.store().delete(slotId);
        render(`${t('ui.expedientes.newGame')} ${slotId}`);
      } else {
        opener.focus();
      }
    };
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close(false);
    });
    dialog.querySelector('[data-choice="cancel"]')!.addEventListener('click', () => close(false));
    dialog.querySelector('[data-choice="confirm"]')!.addEventListener('click', () => close(true));
    section.append(dialog);
    dialog.showModal();
    dialog.querySelector<HTMLElement>('[data-choice="cancel"]')!.focus();
  };

  section.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button[data-action]');
    if (!button) return;
    const slotId = button.closest<HTMLElement>('[data-slot]')!.dataset.slot as SlotId;
    if (button.dataset.action === 'new') {
      options.store().newGame(slotId);
      options.onSlotReady(slotId);
    } else if (button.dataset.action === 'continue') {
      options.store().load(slotId);
      options.onSlotReady(slotId);
    } else {
      confirmDelete(slotId, button);
    }
  });

  render();
  return section;
}
